import { datePickerAr, datePickerFr } from './i18n/datePicker';

export const toSafeNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.').trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  return fallback;
};

export const toSafeDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** True when locale tag is Arabic (ar, ar-MR, ar-MA, …). */
export const isArabicLocale = (locale = '') =>
  String(locale).toLowerCase().startsWith('ar');

/** Gregorian Arabic display: translated labels, Western digits (0–9). */
export const ARABIC_DISPLAY_LOCALE = 'ar-MR-u-nu-latn';

const FRENCH_DISPLAY_LOCALE = 'fr-FR';

export const DATE_DISPLAY_OPTIONS = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

export const TIME_DISPLAY_OPTIONS = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const EASTERN_ARABIC_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function resolveFrenchLocale(locale) {
  return isArabicLocale(locale) ? FRENCH_DISPLAY_LOCALE : (locale || FRENCH_DISPLAY_LOCALE);
}

function wantsNamedMonth(monthOpt) {
  return monthOpt === 'short' || monthOpt === 'long';
}

/** Arabic calendar label with translated month names + Western digits (0–9). */
export function formatArabicDisplayDate(date, options = {}) {
  const safe = toSafeDate(date);
  if (!safe) return '';

  const parts = [];

  if (options.weekday) {
    const weekdayLen = options.weekday === 'long' ? 'long' : 'short';
    const fromIntl = safe.toLocaleDateString(
      ARABIC_DISPLAY_LOCALE,
      withWesternDigits(ARABIC_DISPLAY_LOCALE, { weekday: weekdayLen })
    );
    const fromList = datePickerAr.weekdayNamesShort?.[safe.getDay()];
    parts.push(toWesternDigits(fromIntl || fromList || ''));
  }

  if (options.day !== undefined) {
    const dayVal = options.day === '2-digit' ? pad2(safe.getDate()) : String(safe.getDate());
    parts.push(toWesternDigits(dayVal));
  }

  if (options.month !== undefined) {
    const monthIdx = safe.getMonth();
    if (options.month === '2-digit') {
      parts.push(toWesternDigits(pad2(monthIdx + 1)));
    } else if (options.month === 'long' || options.month === 'short') {
      parts.push(datePickerAr.monthNames[monthIdx] || '');
    } else {
      parts.push(toWesternDigits(pad2(monthIdx + 1)));
    }
  }

  if (options.year !== undefined) {
    parts.push(toWesternDigits(String(safe.getFullYear())));
  }

  return parts.filter(Boolean).join(' ').trim();
}

/** French calendar via Intl (jour mois année) + Western digits. */
export function formatFrenchDisplayDate(date, options = {}) {
  const safe = toSafeDate(date);
  if (!safe) return '';

  try {
    return toWesternDigits(
      safe.toLocaleDateString(
        FRENCH_DISPLAY_LOCALE,
        withWesternDigits(FRENCH_DISPLAY_LOCALE, options)
      )
    );
  } catch {
    const parts = [];
    if (options.day !== undefined) {
      parts.push(options.day === '2-digit' ? pad2(safe.getDate()) : String(safe.getDate()));
    }
    if (wantsNamedMonth(options.month)) {
      const shortMonths = datePickerFr.monthNamesShort || datePickerFr.monthNames;
      parts.push(shortMonths[safe.getMonth()] || '');
    } else if (options.month !== undefined) {
      parts.push(pad2(safe.getMonth() + 1));
    }
    if (options.year !== undefined) {
      parts.push(String(safe.getFullYear()));
    }
    return parts.join(' ').trim();
  }
}

/** Fixed YYYY/MM/DD — immune to RTL bidi reordering (tables/PDF only). */
export function formatFixedDateYMD(value, fallback = '-') {
  const date = toSafeDate(value);
  if (!date) return fallback;
  return `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}`;
}

/** @deprecated Use formatFixedDateYMD */
export const formatFixedDateDMY = formatFixedDateYMD;

/** Fixed HH:MM:SS (24h) — immune to RTL bidi reordering. */
export function formatFixedTimeHMS(value, fallback = '-') {
  const date = toSafeDate(value);
  if (!date) return fallback;
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

/** Fixed YYYY/MM/DD + newline + HH:MM:SS for tables/PDF. */
export function formatFixedDateTimeMultiline(value, fallback = '-') {
  const datePart = formatFixedDateYMD(value, '');
  const timePart = formatFixedTimeHMS(value, '');
  if (!datePart && !timePart) return fallback;
  if (!timePart) return datePart || fallback;
  if (!datePart) return timePart;
  return `${datePart}\n${timePart}`;
}

/** Force 0–9 digits even when locale or PDF renderer emits Arabic-Indic numerals. */
export function toWesternDigits(value) {
  let s = String(value ?? '');
  for (let i = 0; i < 10; i += 1) {
    s = s.split(ARABIC_INDIC_DIGITS[i]).join(String(i));
    s = s.split(EASTERN_ARABIC_DIGITS[i]).join(String(i));
  }
  return s;
}

export const getDisplayLocale = (isRTL) => (isRTL ? ARABIC_DISPLAY_LOCALE : FRENCH_DISPLAY_LOCALE);

/**
 * Arabic UI uses Western digits (0–9) everywhere — never Arabic-Indic numerals.
 * @param {string} locale
 * @param {Intl.DateTimeFormatOptions} [options]
 */
export const withWesternDigits = (locale, options = {}) => {
  if (!isArabicLocale(locale)) return { ...options };
  return { ...options, numberingSystem: 'latn' };
};

export const formatSafeDate = (value, locale = 'fr-MR', options = {}, fallback = '-') => {
  const date = toSafeDate(value);
  if (!date) return fallback;

  if (options.fixedNumeric) {
    return formatFixedDateYMD(value, fallback);
  }

  const opts = { ...DATE_DISPLAY_OPTIONS, ...options };

  if (isArabicLocale(locale)) {
    if (wantsNamedMonth(opts.month)) {
      return formatArabicDisplayDate(date, opts) || fallback;
    }
    return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
  }

  return formatFrenchDisplayDate(date, opts) || fallback;
};

export const formatSafeTime = (value, locale = 'fr-MR', options = {}, fallback = '-') => {
  const date = toSafeDate(value);
  if (!date) return fallback;

  if (options.fixedNumeric) {
    return formatFixedTimeHMS(value, fallback);
  }

  try {
    const formatted = date.toLocaleTimeString(
      resolveFrenchLocale(locale),
      withWesternDigits(locale, { ...TIME_DISPLAY_OPTIONS, ...options })
    );
    return toWesternDigits(formatted);
  } catch {
    return formatFixedTimeHMS(value, fallback);
  }
};

export const formatSafeDateTime = (value, locale = 'fr-MR', options = {}, fallback = '-') => {
  const date = toSafeDate(value);
  if (!date) return fallback;

  if (options.fixedNumeric) {
    return `${formatFixedDateYMD(value, '')} ${formatFixedTimeHMS(value, '')}`.trim() || fallback;
  }

  const {
    fixedNumeric: _fixed,
    hour,
    minute,
    second,
    hour12,
    timeZone,
    timeZoneName,
    ...dateOpts
  } = options;
  const datePart = formatSafeDate(value, locale, dateOpts, '');
  const timePart = formatSafeTime(value, locale, {
    hour: hour ?? '2-digit',
    minute: minute ?? '2-digit',
    second,
    hour12,
    timeZone,
    timeZoneName,
  }, '');

  const combined = [datePart, timePart].filter(Boolean).join(' ').trim();
  return combined || fallback;
};

/** Date + time on two lines for tables and PDF export (always YYYY/MM/DD). */
export const formatSafeDateTimeMultiline = (value, locale = 'fr-MR', fallback = '-') =>
  formatFixedDateTimeMultiline(value, fallback);

/** Short date for export/report titles (e.g. "juil. 2026" / "يوليو 2026"). */
export const formatReportTitleDate = (
  locale,
  date = new Date(),
  options = { month: 'short', year: 'numeric' }
) => {
  const safe = toSafeDate(date);
  if (!safe) return '';

  if (isArabicLocale(locale)) {
    const month = datePickerAr.monthNames[safe.getMonth()] || '';
    const year = toWesternDigits(String(safe.getFullYear()));
    if (options.day !== undefined) {
      const day = toWesternDigits(String(safe.getDate()));
      return `${day} ${month} ${year}`.trim();
    }
    return `${month} ${year}`.trim();
  }

  try {
    return toWesternDigits(
      safe.toLocaleDateString(FRENCH_DISPLAY_LOCALE, withWesternDigits(FRENCH_DISPLAY_LOCALE, options))
    );
  } catch {
    const month = (datePickerFr.monthNamesShort || datePickerFr.monthNames)[safe.getMonth()] || '';
    return `${month} ${safe.getFullYear()}`.trim();
  }
};

export const formatSafeMonthYear = (locale = FRENCH_DISPLAY_LOCALE) =>
  formatReportTitleDate(locale, new Date(), { month: 'short', year: 'numeric' });

/** Chart axis: weekday + day + month (localized). */
export const formatChartDayLabel = (date = new Date(), isRTL = false) => {
  const safe = toSafeDate(date) || new Date();
  const locale = getDisplayLocale(isRTL);

  if (isRTL) {
    const weekday =
      datePickerAr.weekdayNamesShort?.[safe.getDay()] ||
      toWesternDigits(
        safe.toLocaleDateString(
          ARABIC_DISPLAY_LOCALE,
          withWesternDigits(ARABIC_DISPLAY_LOCALE, { weekday: 'short' })
        )
      );
    const month = datePickerAr.monthNames[safe.getMonth()] || '';
    return `${weekday} ${toWesternDigits(String(safe.getDate()))} ${month}`.trim();
  }

  return formatFrenchDisplayDate(safe, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};
